import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { Trans, useTranslation } from "react-i18next";
import { Button } from "components/bootstrap";
import ValidatedInput from "integrations/aws/common/ValidatedInput";
import FormWrap from "integrations/aws/common/FormWrap";
import { ApiRoutes } from "integrations/aws/common/Routes";
import { renderOptions } from "integrations/aws/common/Options";
import useFetch from "integrations/hooks/useFetch";
import formValidation from "integrations/aws/utils/formValidation";
import FormDataContext from "integrations/contexts/FormDataContext";
import { ApiContext } from "integrations/aws/context/Api";
import { toAWSRequest } from "integrations/aws/common/formDataAdapter";
import SetupModal from "./setup-steps/SetupModal";
const BackButton = styled(Button)`
  margin-right: 9px;
`;
const DocsAnchor = ({ href, children }) => /* @__PURE__ */ React.createElement("a", { href, target: "_blank", rel: "noopener noreferrer" }, children);
const KinesisSetup = ({ onChange, onSubmit, toggleSetup = null }) => {
  const { t } = useTranslation("integrations");
  const { availableGroups, setGroups, clearLogData } = useContext(ApiContext);
  const { formData } = useContext(FormDataContext);
  const [formError, setFormError] = useState(null);
  const [disabledForm, setDisabledForm] = useState(false);
  const [disabledGroups, setDisabledGroups] = useState(false);
  const [showTOS, setShowTOS] = useState(false);
  const [groupNamesStatus, setGroupNamesUrl] = useFetch(
    ApiRoutes.INTEGRATIONS.AWS.CLOUDWATCH.GROUPS,
    (response) => {
      setGroups(response);
    },
    "POST",
    toAWSRequest(formData, { region: formData.awsCloudWatchAwsRegion.value })
  );
  useEffect(() => {
    if (groupNamesStatus.error) {
      setGroupNamesUrl(null);
      const noGroups = /No CloudWatch log groups/g;
      if (groupNamesStatus.error.match(noGroups)) {
        setFormError({
          full_message: groupNamesStatus.error,
          nice_message: /* @__PURE__ */ React.createElement("span", null, t("aws_kinesis_setup_new_stream.no_groups_error"))
        });
        setDisabledGroups(true);
      } else {
        setFormError({
          full_message: groupNamesStatus.error
        });
      }
    }
    return () => {
      setGroups({ log_groups: [] });
    };
  }, [groupNamesStatus.error, setGroupNamesUrl, setGroups, t]);
  const handleAgreeSubmit = () => {
    clearLogData();
    onSubmit();
  };
  const handleFormSubmit = () => {
    setDisabledForm(true);
    setShowTOS(true);
  };
  const handleAgreeCancel = () => {
    setDisabledForm(false);
    setShowTOS(false);
  };
  return /* @__PURE__ */ React.createElement(
    FormWrap,
    {
      onSubmit: handleFormSubmit,
      buttonContent: t("aws_kinesis_setup_new_stream.begin_button"),
      disabled: formValidation.isFormValid(["awsCloudWatchKinesisStream", "awsCloudWatchAwsGroupName"], formData) || disabledForm,
      loading: groupNamesStatus.loading,
      error: formError,
      title: t("aws_kinesis_setup_new_stream.title"),
      description: ""
    },
    /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement(
      Trans,
      {
        t,
        i18nKey: "aws_kinesis_setup_new_stream.intro",
        components: {
          docsLink: /* @__PURE__ */ React.createElement(DocsAnchor, { href: "https://docs.aws.amazon.com/AmazonCloudWatch/latest/logs/SubscriptionFilters.html" })
        }
      }
    )),
    /* @__PURE__ */ React.createElement("ol", null, /* @__PURE__ */ React.createElement("li", null, t("aws_kinesis_setup_new_stream.step_create_stream")), /* @__PURE__ */ React.createElement("li", null, t("aws_kinesis_setup_new_stream.step_create_role")), /* @__PURE__ */ React.createElement("li", null, t("aws_kinesis_setup_new_stream.step_subscribe"))),
    /* @__PURE__ */ React.createElement(
      ValidatedInput,
      {
        id: "awsCloudWatchKinesisStream",
        type: "text",
        label: t("aws_kinesis_setup_new_stream.stream_name_label"),
        placeholder: t("aws_kinesis_setup_new_stream.stream_name_placeholder"),
        onChange,
        fieldData: formData.awsCloudWatchKinesisStream,
        disabled: disabledForm,
        pattern: "[a-zA-Z0-9_.-]{1,128}$",
        help: t("aws_kinesis_setup_new_stream.stream_name_help"),
        required: true
      }
    ),
    /* @__PURE__ */ React.createElement(
      ValidatedInput,
      {
        id: "awsCloudWatchAwsGroupName",
        type: "select",
        fieldData: formData.awsCloudWatchAwsGroupName,
        onChange,
        label: t("aws_kinesis_setup_new_stream.group_name_label"),
        required: true,
        disabled: groupNamesStatus.loading || disabledGroups || disabledForm
      },
      renderOptions(availableGroups, t("aws_kinesis_setup_new_stream.choose_group"), groupNamesStatus.loading)
    ),
    toggleSetup && /* @__PURE__ */ React.createElement(BackButton, { onClick: toggleSetup, type: "button", disabled: disabledForm }, t("aws_kinesis_setup_new_stream.back_button")),
    showTOS && /* @__PURE__ */ React.createElement(
      SetupModal,
      {
        onSubmit: handleAgreeSubmit,
        onCancel: handleAgreeCancel,
        groupName: formData.awsCloudWatchAwsGroupName.value,
        streamName: formData.awsCloudWatchKinesisStream.value
      }
    )
  );
};
export default KinesisSetup;
